import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import MenuItem from 'flow/components/ui-components/MenuItem';
import NestedMenuItem from 'flow/components/ui-components/NestedMenuItem';
import SearchInput from 'flow/components/ui-components/SearchInput';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import {
    selectTicketKeysMineSorted,
    selectTicketViewIsMounted,
    selectTicketsListTabSelected,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { setSnackbar } from 'flow/actions/app-actions';
import { forwardTicketToQueue } from 'flow/actions/tickets-action';
import { selectQueuesSortingDataSortedByFavorite } from 'flow/selectors/queue-selectors';
import { State } from 'flow/types/State';
import { AppDispatch } from 'flow/types/Redux';
import { QueueKey, TicketKey } from 'flow/types/EntityKeys';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import {
    TICKETS_LIST_TABS_KEYS,
    getEntityKeyNextKey,
} from 'flow/utils/ticket-utils';
import {
    filterQueuesBySearch,
    shouldShowQueueSearch,
} from './forward-to-queue-utils';
import useTicketAction from 'flow/hooks/useTicketAction';

type Props = {
    ticketKey: TicketKey;
};

const handlePostForwardNavigation =
    (ticketKey: TicketKey) =>
    (_dispatch: AppDispatch, getState: () => State) => {
        const state = getState();
        const tabSelected = selectTicketsListTabSelected(state);
        const ticketKeysMyTicketsTab = selectTicketKeysMineSorted(state);
        const ticketIsMounted = selectTicketViewIsMounted(state, ticketKey);

        if (
            tabSelected === TICKETS_LIST_TABS_KEYS.MY_TICKETS &&
            ticketIsMounted
        ) {
            const nextKey = getEntityKeyNextKey(
                ticketKeysMyTicketsTab,
                ticketKey,
            );
            if (nextKey) {
                history.push(
                    generatePath(routes['tickets.ticketKey'], {
                        ticketKey: nextKey,
                    }),
                );
            } else {
                history.push(routes['tickets']);
            }
        }
    };

const MenuItemForwardToQueueMenu = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { hasFeature } = useFeatureFlags();
    const [search, setSearch] = useState('');

    const canForwardToQueue = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'forwardToQueue'),
    );

    const sortedQueues = useAppSelector(
        selectQueuesSortingDataSortedByFavorite,
    );

    const showSearch = shouldShowQueueSearch(sortedQueues);

    const filteredQueues = useMemo(
        () => filterQueuesBySearch(sortedQueues, search),
        [sortedQueues, search],
    );

    if (
        !hasFeature(FeatureFlags.TICKETS_FORWARD_TO_QUEUE) ||
        !canForwardToQueue ||
        sortedQueues.isEmpty()
    ) {
        return null;
    }

    const handleForwardToQueue = (
        event: React.MouseEvent,
        queueKey: QueueKey,
    ) => {
        event?.stopPropagation();
        runTicketAction(forwardTicketToQueue(ticketKey, queueKey))
            .then(() => {
                dispatch(handlePostForwardNavigation(ticketKey));
            })
            .catch((error: any) => {
                if (error?.status === 400) {
                    dispatch(
                        setSnackbar({
                            i18nextMessage: 'ticket.forward.queue_not_eligible',
                        }),
                    );
                }
            });
    };

    const searchHeader = showSearch ? (
        <div className="mb-2">
            <SearchInput
                iconKey="search"
                value={search}
                placeholder={i18next.t('input.search')}
                onChange={(event) => setSearch(event.target.value)}
                // Focus on open so the user can type without first clicking,
                // which also avoids a mouse trip across the hover-controlled
                // submenu (leaving the submenu closes it).
                autoFocus={true}
                // Stop click/keydown from bubbling to the parent menu, which
                // would otherwise close the menu or trigger Headless UI's
                // typeahead.
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                className="h-9"
            />
        </div>
    ) : null;

    return (
        <NestedMenuItem
            title={i18next.t('ticket.forward.to')}
            // Anchor the submenu's top to the parent row (instead of the
            // default vertically-centered 'right'). Otherwise, filtering the
            // list down to a single result collapses the panel height and
            // re-centers it on the row, making the result jump away from the
            // cursor and detach from the menu.
            placementDesired="right-start"
            header={searchHeader}
            listClassName={showSearch ? 'max-h-[400px]' : undefined}
            // Reset the filter when the submenu closes so a stale term doesn't
            // persist into the next opening.
            onOpenChange={(open) => {
                if (!open) {
                    setSearch('');
                }
            }}
        >
            {filteredQueues.length === 0 ? (
                <div className="w-56 p-2 text-gray-400">
                    {i18next.t('general.search.no-results', { search })}
                </div>
            ) : (
                filteredQueues.map((queue) => {
                    const queueKey = queue.get('key');
                    return (
                        <MenuItem
                            key={queueKey}
                            onClick={(event) =>
                                handleForwardToQueue(event, queueKey)
                            }
                            className="w-56"
                        >
                            {queue.get('description')}
                        </MenuItem>
                    );
                })
            )}
        </NestedMenuItem>
    );
};

export default MenuItemForwardToQueueMenu;
